// Px3 Pipeline - the allocation pool.
//
// The panels preflight is allowed to put mailboxes on, and how much room is left
// on them. Every number here comes off the API as-is; nothing is recomputed in
// the browser, because the eligibility rules (DWD authorised, admin licence
// removed, final password held, no unfinished migration item, not excluded) live
// in one SQL statement and a second opinion drawn here would eventually
// contradict the allocator.
//
// The histogram's honest caveat, repeated on screen: `unknown` is a panel that
// has never been probed. It is not a full panel and it is not a free one - the
// allocator re-probes anything stale before it trusts a number, and so should
// the reader.

import { useCallback, useEffect, useState } from 'react';

import type { Px3ShowToast } from '../Px3';
import {
  PX3_HISTOGRAM_BUCKETS,
  px3Api,
  px3ErrorMessage,
  type Px3Pool,
} from '../px3Api';

/** Same 10s as the other list screens. */
const POLL_MS = 10_000;

/** What each bucket means in seats, so nobody has to guess whether "18" is a
 *  count of panels or of seats. */
const BUCKET_HINT: Record<string, string> = {
  '0': 'full',
  '1-2': 'scraps',
  '3-5': 'small',
  '6-11': 'partial',
  '12-17': 'mostly free',
  '18': 'whole panel',
  unknown: 'never probed',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Whitespace, commas or newlines: an operator pastes from a spreadsheet, a
 *  Slack message or a psql result and all three should work. Deduplicated
 *  keep-first, lower-cased to match the column. */
function parseEmails(text: string): string[] {
  const seen = new Set<string>();
  for (const raw of text.split(/[\s,;]+/)) {
    const email = raw.trim().toLowerCase();
    if (email !== '') seen.add(email);
  }
  return [...seen];
}

export function PoolView({ showToast }: { showToast: Px3ShowToast }): JSX.Element {
  const [pool, setPool] = useState<Px3Pool | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emailsText, setEmailsText] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [probing, setProbing] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setPool(await px3Api.getPool());
      setError(null);
    } catch (err) {
      setError(px3ErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const parsed = parseEmails(emailsText);

  async function addExclusions(): Promise<void> {
    if (parsed.length === 0) return;
    setAdding(true);
    try {
      const { added } = await px3Api.addExclusions(parsed, reason.trim());
      setEmailsText('');
      setReason('');
      await refresh();
      // `added` counts rows actually inserted. Saying "3 excluded" when one was
      // already on the list would hide that its ORIGINAL reason and author were
      // kept, not overwritten by what was just typed.
      showToast(
        added === parsed.length ? 'success' : 'warn',
        added === parsed.length
          ? `${added} panel(s) excluded from the pool.`
          : `${added} of ${parsed.length} added. The rest were already excluded and kept their original reason.`,
      );
    } catch (err) {
      showToast('error', px3ErrorMessage(err));
    } finally {
      setAdding(false);
    }
  }

  async function removeExclusion(email: string): Promise<void> {
    setRemoving(email);
    try {
      await px3Api.removeExclusion(email);
      await refresh();
      showToast('success', `${email} is back in the pool.`);
    } catch (err) {
      showToast('error', px3ErrorMessage(err));
    } finally {
      setRemoving(null);
    }
  }

  async function refreshOccupancy(): Promise<void> {
    setProbing(true);
    try {
      await px3Api.refreshPool();
      showToast(
        'info',
        'Occupancy probe queued. It walks the whole eligible pool, so the counts below update over the next few minutes, not instantly.',
      );
    } catch (err) {
      showToast('error', px3ErrorMessage(err));
    } finally {
      setProbing(false);
    }
  }

  const histogram = pool?.occupancy_histogram ?? {};
  const peak = Math.max(1, ...PX3_HISTOGRAM_BUCKETS.map((b) => histogram[b] ?? 0));

  return (
    <>
      {error && (
        <div className="px3-toast px3-toast--error">Could not read the pool: {error}</div>
      )}

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">
            Eligible panels
            {pool !== null && (
              <span className="px3-badge px3-badge--ok" style={{ marginLeft: 10 }}>
                {pool.eligible}
              </span>
            )}
          </h2>
          <div className="px3-actions">
            <button
              type="button"
              className="px3-btn px3-btn--ghost px3-btn--small"
              onClick={() => void refresh()}
            >
              Refresh
            </button>
            <button
              type="button"
              className="px3-btn px3-btn--secondary px3-btn--small"
              disabled={probing}
              onClick={() => void refreshOccupancy()}
            >
              {probing ? 'Queueing...' : 'Refresh occupancy'}
            </button>
          </div>
        </div>
        <div className="px3-card-body">
          <span className="px3-hint">
            Free seats per panel, bucketed the way the allocator thinks: 18 is a whole empty panel,
            the small buckets are the scraps a tail of domains lands in. Counts are cached;
            "Refresh occupancy" enqueues a live probe across the pool.
          </span>
          {pool === null ? (
            <div className="px3-empty">{error === null ? 'Loading pool...' : 'No pool data.'}</div>
          ) : (
            <div className="px3-hist">
              {PX3_HISTOGRAM_BUCKETS.map((bucket) => {
                const count = histogram[bucket] ?? 0;
                const fill =
                  bucket === 'unknown'
                    ? 'px3-hist-fill--unknown'
                    : bucket === '0'
                      ? 'px3-hist-fill--empty'
                      : '';
                return (
                  <div key={bucket} className="px3-hist-row">
                    <span className="px3-hist-label" title={BUCKET_HINT[bucket]}>
                      {bucket === 'unknown' ? 'unknown' : `${bucket} free`}
                    </span>
                    <div className="px3-hist-track">
                      <div
                        className={`px3-hist-fill ${fill}`}
                        style={{ width: `${Math.round((count / peak) * 100)}%` }}
                      />
                    </div>
                    <span className="px3-hist-count">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
          {(histogram['unknown'] ?? 0) > 0 && (
            <div className="px3-notice px3-notice--warn">
              {histogram['unknown']} eligible panel(s) have never been probed. They are not full and
              they are not free - preflight re-probes stale panels before it allocates, so treat
              this row as "unread", not as capacity.
            </div>
          )}
        </div>
      </section>

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">
            Exclusions {pool !== null && `(${pool.exclusions.length})`}
          </h2>
          <span className="px3-hint">
            An excluded panel is invisible to preflight. Excluding one does NOT touch mailboxes an
            order has already placed on it.
          </span>
        </div>
        <div className="px3-card-body">
          <div className="px3-field">
            <label className="px3-field-label" htmlFor="px3-excl-emails">
              Panel admin addresses
            </label>
            <textarea
              id="px3-excl-emails"
              className="px3-textarea px3-mono"
              value={emailsText}
              placeholder={'admin@omnimedia7534.shop\nadmin@omnimedia7535.shop'}
              onChange={(e) => setEmailsText(e.target.value)}
              spellCheck={false}
            />
            <span className="px3-hint">
              One per line, or comma separated. {parsed.length} address(es) parsed.
            </span>
          </div>
          <div className="px3-field">
            <label className="px3-field-label" htmlFor="px3-excl-reason">
              Reason
            </label>
            <input
              id="px3-excl-reason"
              className="px3-input"
              type="text"
              value={reason}
              maxLength={300}
              placeholder="reserved for the Serken batch 4 destinations"
              onChange={(e) => setReason(e.target.value)}
            />
            <span className="px3-hint">
              Optional, but the next operator reading this list will want it.
            </span>
          </div>
          <div className="px3-actions">
            <button
              type="button"
              className="px3-btn px3-btn--primary"
              disabled={adding || parsed.length === 0}
              onClick={() => void addExclusions()}
            >
              {adding ? 'Adding...' : `Exclude ${parsed.length || ''} panel(s)`}
            </button>
          </div>
        </div>

        <div className="px3-table-wrap">
          <table className="px3-table">
            <thead>
              <tr>
                <th>Panel</th>
                <th>Reason</th>
                <th>Excluded</th>
                <th className="px3-col-center" />
              </tr>
            </thead>
            <tbody>
              {pool === null || pool.exclusions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px3-empty">
                    {pool === null ? 'Loading...' : 'No panels are excluded. The whole eligible pool is in play.'}
                  </td>
                </tr>
              ) : (
                pool.exclusions.map((x) => (
                  <tr key={x.panel_email}>
                    <td className="px3-cell-mono">{x.panel_email}</td>
                    <td className="px3-cell-faint">{x.reason ?? '-'}</td>
                    <td className="px3-cell-faint">{fmtDate(x.created_at)}</td>
                    <td className="px3-col-center">
                      <button
                        type="button"
                        className="px3-btn px3-btn--ghost px3-btn--small"
                        disabled={removing === x.panel_email}
                        onClick={() => void removeExclusion(x.panel_email)}
                      >
                        {removing === x.panel_email ? 'Removing...' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
